import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Interaction state (Linear Algebra: Input Vector)
  const mouse = useRef({ x: -1000, y: -1000 }); // Start off-screen

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    window.addEventListener('resize', resize);
    resize();

    // Star properties optimization (Data Structure Choice & Linear Algebra)
    // Use Float32Array for contiguous memory access (better cache locality than arrays of objects)
    const starCount = 100; // Reduced for scroll performance
    const DATA_PER_STAR = 5; // x, y, size, speed, phase
    const starData = new Float32Array(starCount * DATA_PER_STAR);

    // Initialize stars
    for (let i = 0; i < starCount; i++) {
      const idx = i * DATA_PER_STAR;
      starData[idx] = Math.random() * width;     // x
      starData[idx + 1] = Math.random() * height; // y
      starData[idx + 2] = Math.random() * 1.5;   // size
      starData[idx + 3] = Math.random() * 0.2 + 0.05; // speed
      starData[idx + 4] = Math.random() * Math.PI * 2; // phase (for sine wave)
    }

    // Interaction state (Linear Algebra: Input Vector)
    // Mouse ref moved to top level

    const handleMouseMove = (e: MouseEvent) => {
      mouse.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);

    let animationFrameId: number;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      // Update and draw stars
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      
      const time = Date.now() * 0.001; // Current time in seconds

      for (let i = 0; i < starCount; i++) {
        const idx = i * DATA_PER_STAR;
        
        // Base physics
        const speed = starData[idx + 3];
        let x = starData[idx];
        let y = starData[idx + 1];
        const size = starData[idx + 2];
        const phase = starData[idx + 4];

        // 1. Base Gradient Motion (Linear Algebra: Translation)
        y -= speed;
        
        // Reset if off screen
        if (y < 0) {
          y = height;
          x = Math.random() * width;
        }

        // 2. Interactive Force Field (Calculus 3: Vector Fields)
        // Calculate vector from mouse to star
        const dx = x - mouse.current.x;
        const dy = y - mouse.current.y;
        // Euclidean distance (Linear Algebra: Norm)
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // Apply repulsive force if within radius (Optimization: spatial pruning)
        // Force magnitude follows inverse distance curve (Calculus: Limits/Decay)
        const interactionRadius = 150;
        if (dist < interactionRadius) {
          const force = (interactionRadius - dist) / interactionRadius;
          const repulsionStrength = 8 * force; // Scaler multiplier
          
          // Apply normalized vector * force
          x += (dx / dist) * repulsionStrength;
          y += (dy / dist) * repulsionStrength;
        }

        // Write back new position
        starData[idx] = x;
        starData[idx + 1] = y;

        // 3. Twinkle effect (Calculus 2: Periodic Function)
        const opacity = 0.5 + 0.3 * Math.sin(time * 2 + phase);

        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
        background: 'linear-gradient(to bottom, #000022 0%, #000044 100%)', // Deep space-like gradient fallback/base
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </Box>
  );
};

export default ParticleBackground;
